import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import Relay from "@/relay/relayUtils"
import GradientLayerIcon from "@assets/disco/icons/gradient/gradient-layer.svg"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoEmptyState, DiscoSpinner, DiscoText } from "@disco-ui"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"
import CreateTrainingSourceButton from "./CreateTrainingSourceButton"
import TrainingSourceTableListItem from "./TrainingSourceTableListItem"
import {
  TrainingSourceKind,
  TrainingSourceTablePaginationQuery,
} from "./__generated__/TrainingSourceTablePaginationQuery.graphql"
import { TrainingSourceTableQuery } from "./__generated__/TrainingSourceTableQuery.graphql"
import { TrainingSourceTable_PaginationFragment$key } from "./__generated__/TrainingSourceTable_PaginationFragment.graphql"

const ITEMS_PER_LOAD = 10

interface Props {
  kind: TrainingSourceKind
  search?: string
}

function TrainingSourceTable({ kind, search }: Props) {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()

  const { node } = useLazyLoadQuery<TrainingSourceTableQuery>(
    graphql`
      query TrainingSourceTableQuery(
        $id: ID!
        $first: Int!
        $after: String
        $search: String
        $kinds: [TrainingSourceKind!]
      ) {
        node(id: $id) {
          ... on Organization {
            ...TrainingSourceTable_PaginationFragment
              @arguments(first: $first, after: $after, search: $search, kinds: $kinds)
          }
        }
      }
    `,
    { id: activeOrganization.id, first: ITEMS_PER_LOAD, search, kinds: [kind] },
    { fetchPolicy: "store-and-network" }
  )

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    TrainingSourceTablePaginationQuery,
    TrainingSourceTable_PaginationFragment$key
  >(
    graphql`
      fragment TrainingSourceTable_PaginationFragment on Organization
      @refetchable(queryName: "TrainingSourceTablePaginationQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
        kinds: { type: "[TrainingSourceKind!]" }
      ) {
        trainingSources(first: $first, after: $after, search: $search, kinds: $kinds)
          @connection(key: "TrainingSourceTable_trainingSources") {
          totalCount
          edges {
            node {
              id
              status
              ...TrainingSourceTableListItemFragment
            }
          }
        }
      }
    `,
    node
  )

  const trainingSources = Relay.connectionToArray(data?.trainingSources)

  return (
    <div className={classes.list}>
      <div className={classes.header}>
        <DiscoText variant={"body-xs-600"}>{"Name"}</DiscoText>
        <DiscoText variant={"body-xs-600"}>{"Date Created"}</DiscoText>
        <DiscoText variant={"body-xs-600"}>{"Pages"}</DiscoText>
        <DiscoText variant={"body-xs-600"}>{"Status"}</DiscoText>
        <div className={classes.headerPlaceholder} />
      </div>
      {trainingSources.length ? (
        trainingSources.map((source) => (
          <TrainingSourceTableListItem key={source.id} trainingSourceKey={source} />
        ))
      ) : (
        <DiscoEmptyState
          testid={"TrainingSourceTable.empty-state"}
          icon={<GradientLayerIcon className={classes.icon} />}
          buttons={
            <CreateTrainingSourceButton
              variant={"contained"}
              kind={kind}
              color={"primary"}
            >
              {`New ${kind === "web_crawl" ? "Link" : "Knowledge Base"} `}
            </CreateTrainingSourceButton>
          }
          title={`No ${kind === "web_crawl" ? "links" : "knowledge bases"} added `}
          subtitle={
            kind === "web_crawl"
              ? "Add articles, references, and other webpages to help Disco AI stay aligned with your subject matter and context."
              : "Add an external knowledge like Notion, Zendesk, or Pylon to help Disco AI stay aligned with your subject matter and context."
          }
        />
      )}
      {hasNext && (
        <DiscoScrolledIntoView
          isLoading={isLoadingNext}
          onScrolledIntoView={() => loadNext(ITEMS_PER_LOAD)}
        />
      )}
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  header: {
    display: "grid",
    gridTemplateColumns: "1fr 95px 55px 95px 36px",
    borderRadius: theme.measure.borderRadius.medium,
    backgroundColor: theme.palette.groovy.neutral[200],
    padding: theme.spacing(1),

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 90px 40px 60px 35px",
    },
  },
  list: {
    marginTop: theme.spacing(2),
  },
  icon: {
    height: "77.02px",
    width: "73.27px",
    margin: "21.42px",
  },
  headerPlaceholder: {
    height: "10px",
    width: "36px",
  },
}))

export default Relay.withSkeleton({
  component: TrainingSourceTable,
  skeleton: () => <DiscoSpinner />,
})
